import { useCallback, useRef } from "react"
import type { MapboxGeoJSONFeature, MapLayerMouseEvent } from "mapbox-gl"
import getCenter from "@turf/center-of-mass"
import { getIGNParcelFromCoordinates, getAddressDetails } from "@/lib/explore/api/geo"
import { getKaptcherParcelle } from "@/lib/explore/api/kaptcher-data"
import type { IParcel, IPermit } from "@/types/explore"

interface UseMapClickHandlerProps {
  selectedParcels: IParcel[]
  isToolOn: boolean
  isStudyMode: boolean
  isParcelFilterOn: boolean
  setSelectedParcels: React.Dispatch<React.SetStateAction<IParcel[]>>
  setSelectedPC: (pc: { permit: IPermit; longitude: number; latitude: number } | null) => void
  setSelectedPLU: (plu: MapboxGeoJSONFeature | null) => void
  setPrescriptionPopup: (popup: { longitude: number; latitude: number; libelle: string; txt: string } | null) => void
  setParcelPOI: (poi: { lon: number | null; lat: number | null }) => void
  setLoading?: (key: string, active: boolean) => void
}

const getParcelId = (p: { properties?: Record<string, unknown> | null }): string | undefined =>
  (p?.properties?.idu as string | undefined) || (p?.properties?.id as string | undefined)

export const useMapClickHandler = (props: UseMapClickHandlerProps) => {
  const {
    isToolOn,
    isStudyMode,
    isParcelFilterOn,
    setSelectedParcels,
    setSelectedPC,
    setSelectedPLU,
    setPrescriptionPopup,
    setLoading,
  } = props

  // Mutable ref for hover state (per hook instance, not module-level)
  const prevFeatureIdRef = useRef<string | number | undefined>(undefined)

  const handleClick = useCallback(
    async (e: MapLayerMouseEvent) => {
      if (isToolOn || isStudyMode) return

      const parcel = e?.features?.find(
        (f: MapboxGeoJSONFeature) => f.layer?.id === "search-parcels" || f.layer?.id === "cadastre-layer",
      )
      const pc = e?.features?.find((f: MapboxGeoJSONFeature) => f.layer?.id === "pcs")
      const plu = e?.features?.find((f: MapboxGeoJSONFeature) => f.layer?.id === "commune-plu")
      const prescriptions = e?.features?.find((f: MapboxGeoJSONFeature) =>
        f.layer ? ["wfs-layer", "wfs-layer-border", "wfs-layer-pct", "wfs-layer-line"].includes(f.layer.id) : false,
      )

      if (prescriptions) {
        setPrescriptionPopup({
          longitude: e.lngLat.lng,
          latitude: e.lngLat.lat,
          libelle: prescriptions?.properties?.libelle ?? "",
          txt: prescriptions?.properties?.txt ?? "",
        })
      }

      if (parcel && parcel.layer?.id === "search-parcels") {
        const parcelleCenter = getCenter(parcel?.geometry)
        const parcelleId = getParcelId(parcel)

        setSelectedParcels((prev) => {
          const existingIndex = prev.findIndex((p) => getParcelId(p) === parcelleId)
          if (existingIndex !== -1) {
            return prev.filter((_, index) => index !== existingIndex)
          }
          const selected = isParcelFilterOn
            ? ({
                geometry: parcel.geometry,
                properties: parcel.properties,
                center: parcelleCenter?.geometry?.coordinates,
                type: parcel.type,
                bbox: parcel.properties?.bbox?.split(",")?.map((b: string) => parseFloat(b)),
              } as unknown as IParcel)
            : ({ ...parcel, geometry: parcel.geometry } as unknown as IParcel)
          return [...prev, selected]
        })
      }

      if ((!parcel || parcel.layer?.id === "cadastre-layer") && !pc) {
        const pointCoords = e.lngLat
        setLoading?.("parcel", true)
        try {
          let parcelle = await getIGNParcelFromCoordinates([pointCoords.lng, pointCoords.lat])
          if (parcelle) {
            const parcelleCenter = getCenter(parcelle?.geometry)
            const parcelleId = getParcelId(parcelle)

            try {
              const kaptcherParcelle = parcelleId ? await getKaptcherParcelle(parcelleId) : null
              if (kaptcherParcelle) {
                parcelle = { ...parcelle, ...kaptcherParcelle }
              }
            } catch (error) {
              console.error("Erreur lors du chargement des donnees Kaptcher:", error)
            }

            const coords = parcelleCenter?.geometry?.coordinates
            const selected = {
              ...parcelle,
              center: coords,
            } as unknown as IParcel

            setSelectedParcels((prev) => {
              const existingIndex = prev.findIndex((p) => getParcelId(p) === parcelleId)
              if (existingIndex !== -1) {
                return prev.filter((_, index) => index !== existingIndex)
              }
              return [...prev, selected]
            })

            // Fetch address asynchronously (non-blocking)
            if (coords) {
              getAddressDetails(coords[0], coords[1])
                .then((addrDetails) => {
                  const fullAddress = addrDetails.label || undefined
                  if (fullAddress) {
                    setSelectedParcels((prev) =>
                      prev.map((p) => (getParcelId(p) === parcelleId ? { ...p, fullAddress } : p)),
                    )
                  }
                })
                .catch(() => {})
            }
          }
        } catch (error) {
          console.error("Erreur lors du chargement de la parcelle IGN:", error)
        } finally {
          setLoading?.("parcel", false)
        }
      }

      if (pc) {
        setSelectedPC({ permit: pc.properties as IPermit, longitude: e.lngLat.lng, latitude: e.lngLat.lat })
      } else {
        setSelectedPC(null)
      }
      if (plu) setSelectedPLU(plu)
    },
    [
      isToolOn,
      isStudyMode,
      isParcelFilterOn,
      setSelectedParcels,
      setSelectedPC,
      setSelectedPLU,
      setPrescriptionPopup,
      setLoading,
    ],
  )

  const onMouseLeave = useCallback((event: MapLayerMouseEvent) => {
    const features = event?.features?.filter(
      (feature) => feature.layer?.id === "search-parcels" || feature.layer?.source === "cadastre",
    )
    features?.forEach((feature) => {
      const featureId = feature?.id
      const source = feature?.layer?.source?.toString()
      if (featureId && source) {
        event.target.setFeatureState(
          { source, id: featureId, sourceLayer: source === "cadastre" ? "parcelles" : undefined },
          { select: false },
        )
      }
    })
    prevFeatureIdRef.current = undefined
  }, [])

  const onMouseHandler = useCallback((event: MapLayerMouseEvent) => {
    const feat = event?.features?.find((f) => f.layer?.id === "search-parcels" || f.layer?.source === "cadastre")
    if (feat) {
      const featureId = feat.id
      const source = feat?.layer?.source?.toString()
      if (featureId && source) {
        event.target.setFeatureState(
          { source, id: featureId, sourceLayer: source === "cadastre" ? "parcelles" : undefined },
          { select: true },
        )
        if (featureId !== prevFeatureIdRef.current) {
          if (prevFeatureIdRef.current !== undefined) {
            if (event.target.getSource("search-parcels")) {
              event.target.setFeatureState(
                { source: "search-parcels", id: prevFeatureIdRef.current },
                { select: false },
              )
            }
            if (event.target.getSource("cadastre")) {
              event.target.setFeatureState(
                { source: "cadastre", id: prevFeatureIdRef.current, sourceLayer: "parcelles" },
                { select: false },
              )
            }
          }
          prevFeatureIdRef.current = featureId
        }
      }
    }
  }, [])

  return { handleClick, onMouseHandler, onMouseLeave }
}
